/**
 * Synthesizer
 * Combines parallel responses into a synthesized answer
 */

import { providers } from '../providers/registry.js';
import { streamChat } from './api.js';
import { store, addMessage, updateMessage, setStreaming } from './appState.js';
import { db } from './db.js';

/**
 * Synthesize multiple AI responses into one coherent answer
 * @param {string} originalQuestion - The user's original question
 * @param {Array<{model: string, content: string}>} responses - Parallel responses
 */
export async function synthesizeResponses(originalQuestion, responses) {
  const active = providers.getActive();
  if (!active) return;

  const state = store.getState();
  const conversationId = state.chat.currentConversationId;
  if (!conversationId) return;

  const synthesisPrompt = `You are a synthesis AI. Multiple AI models were asked the same question. Analyze their responses and provide a comprehensive, synthesized answer that:
1. Identifies areas of agreement
2. Notes any conflicting information
3. Provides the most accurate and complete answer
4. Credits specific insights to their source models when relevant

Original question: "${originalQuestion}"

${responses.map((r) => `--- Response from ${r.model} ---\n${r.content}\n`).join('\n')}

Provide your synthesized response:`;

  // Add synthesis placeholder message
  addMessage({
    conversationId,
    message: {
      role: 'assistant',
      content: '',
      model: 'Synthesizer',
      status: 'streaming',
    },
  });

  const stateAfter = store.getState();
  const conv = stateAfter.conversations.find(c => c.id === conversationId);
  const synthMsg = conv.messages[conv.messages.length - 1];

  setStreaming(true);
  let fullContent = '';

  try {
    for await (const chunk of streamChat({
      provider: active.providerId,
      model: active.model,
      messages: [{ role: 'user', content: synthesisPrompt }],
      temperature: 0.3,
      maxTokens: 4096,
    })) {
      if (chunk.type === 'text') {
        fullContent += chunk.content;
        updateMessage({
          conversationId,
          messageId: synthMsg.id,
          updates: { content: fullContent, status: 'streaming' },
        });
      } else if (chunk.type === 'error') {
        updateMessage({
          conversationId,
          messageId: synthMsg.id,
          updates: { content: chunk.content, status: 'error' },
        });
        break;
      } else if (chunk.type === 'done') {
        break;
      }
    }

    updateMessage({
      conversationId,
      messageId: synthMsg.id,
      updates: { content: fullContent, status: 'complete' },
    });

    const finalConv = store.getState().conversations.find(c => c.id === conversationId);
    await db.saveConversation(finalConv);
  } catch (error) {
    updateMessage({
      conversationId,
      messageId: synthMsg.id,
      updates: { content: `Synthesis error: ${error.message}`, status: 'error' },
    });
  } finally {
    setStreaming(false);
  }
}
